import { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo, memo, type SetStateAction } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTenant } from "@/context/tenant-context";
import { useAuth } from "@/context/auth-context";
import { useWorkspace } from "@/context/workspace-context";
import { useAgentsByCompany } from "@/hooks/use-agents";
import { chatApi, type ChatMessage, type ChatMessagesWindow, type ChatSession, type ChatFileAttachment } from "@/api/chat";
import { chatNotificationsApi } from "@/api/chat-notifications";
import { agentChannelsApi } from "@/api/agent-channels";
import { skillsApi, type AgentSkillRow } from "@/api/skills";
import { queryKeys } from "@/lib/query-keys";
import { ContextDonut, resolveContextWindow } from "@/components/chat/context-donut";
import { AgentAvatar } from "@/components/agent/agent-avatar";
import { activeTaskCount, presenceFor, useAgentPresence } from "@/hooks/use-agent-presence";
import { ArrowUp, Plus, Slash, Bot, User, ChevronDown, ChevronRight, Square, X, FileText, AtSign, Pencil, Trash2, Hash, Sparkles, Settings, Wand2, Loader2, AlertTriangle, RotateCcw, History } from "lucide-react";
import { cn } from "@/lib/utils";
import { agentRoleLabel } from "@/lib/agent-display";
import { type ActivityEvent } from "@/components/chat/activity-timeline";
import { AgentTurn } from "@/components/chat/agent-turn";
import { UserChatAvatar } from "@/components/chat/user-chat-avatar";
import { FileAttachmentChip, PendingImageThumb } from "@/components/chat/attachment-preview";
import { AgentChatAvatar } from "@/components/chat/agent-chat-avatar";
import {
  MessageContextMenu,
  SelectionCopyChip,
  type MessageCopyMenuState,
} from "@/components/chat/message-copy";
import {
  ArtifactProvider,
  ArtifactPanel,
  ArtifactButton,
  WorkspaceTriggerButton,
  useArtifactAutoOpen,
} from "@/components/chat/artifact-panel";
import { detectArtifacts } from "@/components/chat/artifact-detector";
import { CHAT_STARTER_SUGGESTIONS, firstName, timeOfDayGreeting } from "@/lib/chat-start";
import { AgentToolsMenu } from "@/components/chat/agent-tools-menu";
import {
  MentionDropdown,
  MentionInputHighlight,
  buildMentionCandidates,
  fileMentionText,
  useWorkspaceFileMentions,
  type MentionCandidate,
} from "@/components/chat/mention-picker";
import { ChatToolbarTooltip } from "@/components/chat/chat-toolbar-tooltip";
import { useMobileChannelSheet } from "@/components/layout/mobile-channel-context";

/** Input bar: 672px on mobile, ~768px on desktop (matches Codex/Claude) */
const chatWidthClass = "mx-auto max-w-2xl lg:max-w-3xl";
/** Messages column: slightly wider than the input so content has breathing room */
const messagesWidthClass = "mx-auto max-w-4xl lg:max-w-5xl";
/**
 * How many of the newest messages the transcript fetches and polls. Heavily
 * populated sessions (monitor channels posting every few minutes) can hold
 * thousands of rows; fetching and mounting all of them at once blocked the
 * main thread long enough to trigger the browser's "page isn't responding"
 * dialog. Older history loads in pages of this size via "Show earlier
 * messages".
 */
const RECENT_MESSAGES_LIMIT = 100;

/**
 * Rows outside the live poll window for the active session: earlier pages the
 * user loaded on demand, plus rows the sliding newest-N window has moved past
 * (without this, a message would vanish from the transcript the moment a new
 * one pushed it out of the window). Merged by id; freshest data wins.
 */
interface TranscriptArchive {
  sessionId: string | null;
  byId: Map<string, ChatMessage>;
  /** Whether rows older than everything loaded so far exist server-side. */
  hasEarlier: boolean;
  /** True once the user has pulled at least one earlier page — from then on
   *  `hasEarlier` is owned by the paging responses, not the live window. */
  loadedEarlier: boolean;
}

interface ComposerDraft {
  input: string;
  attachments: File[];
}

interface ActiveChatRun {
  optimisticUserMsg: string | null;
  /** Last persisted row before this send; rows after it belong to this turn. */
  optimisticAfterMessageId: string | null;
  streamingContent: string;
  activityEvents: ActivityEvent[];
  streamingAgent: {
    name?: string;
    avatarUrl?: string | null;
  } | null;
}

const workspaceComposerDraftCache: Record<string, ComposerDraft> = {};

const slashDisabledSkillReasons = new Map<string, string>([
  [
    "workflow-execution",
    "Runs only when the platform starts a workflow.",
  ],
]);

export function ChatSurface() {
  const { tenantId, tenantName } = useTenant();
  const { user: authUser } = useAuth();
  const { threadContext, updateThreadContext } = useWorkspace();
  const queryClient = useQueryClient();
  const mobileChannelSheet = useMobileChannelSheet();
  const [activeRuns, setActiveRuns] = useState<Record<string, ActiveChatRun>>({});
  /** Rich activity log for the current turn — one entry per tool invocation,
   *  keyed by toolUseId so started/completed events update the same row. */
  const [slashMenuOpen, setSlashMenuOpen] = useState(false);
  const slashMenuRef = useRef<HTMLDivElement>(null);
  const [mentionState, setMentionState] = useState<{
    active: boolean;
    query: string;
    startIndex: number;
    selectedIndex: number;
  } | null>(null);
  /**
   * Most recent usage event from the live SSE stream. Lets the chat
   * header's ContextDonut update the moment a turn finishes, without
   * waiting for the next polling refetch of `chat-messages` to land.
   * Persisted usage on the assistant message is the source of truth on
   * navigation/refresh; this is just a freshness cache for the active
   * session.
   */
  const [liveUsage, setLiveUsage] = useState<{
    sessionId: string;
    inputTokens: number;
    model: string | null;
  } | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesScrollRef = useRef<HTMLDivElement>(null);
  // Show the "jump to latest" button only when the user has scrolled up away
  // from the bottom of a conversation.
  const [showScrollButton, setShowScrollButton] = useState(false);
  // Right-click copy menu for message bubbles (user + agent).
  const [copyMenu, setCopyMenu] = useState<MessageCopyMenuState | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  // Mirror layer that renders the @mention highlights behind the (transparent)
  // textarea. Its scroll must be kept in lock-step with the textarea, else long
  // input scrolls the textarea while the visible highlight stays pinned to the
  // top (looks like the composer won't scroll).
  const highlightRef = useRef<HTMLDivElement>(null);
  const inputContainerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const abortControllersRef = useRef<Record<string, AbortController>>({});
  const activeSessionIdRef = useRef<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedAgentId = threadContext.agentId ?? null;
  const activeSessionId = threadContext.sessionId ?? null;
  const activeChannelId = threadContext.channelId ?? null;
  const activeRun = activeSessionId ? activeRuns[activeSessionId] : undefined;
  const anySessionSending = Object.keys(activeRuns).length > 0;
  const sending = !!activeRun;
  const streamingHere = !!activeRun;
  const optimisticUserMsg = activeRun?.optimisticUserMsg ?? null;
  const optimisticAfterMessageId = activeRun?.optimisticAfterMessageId ?? null;
  const streamingContent = activeRun?.streamingContent ?? "";
  const activityEvents = activeRun?.activityEvents ?? [];
  const streamingAgent = activeRun?.streamingAgent ?? null;
  useEffect(() => {
    activeSessionIdRef.current = activeSessionId;
  }, [activeSessionId]);
  // "New chat" opens an empty composer without touching the DB — the ?newChat=1
  // flag signals draft intent. The session row is created lazily on first send
  // (see handleSend), so opening New chat never adds an empty session to recents.
  const wantsNewChat = searchParams.get("newChat") === "1";

  const { data: agents = [] } = useAgentsByCompany(tenantId);
  const selectedAgent = agents.find((a) => a.id === selectedAgentId);
  const { data: presence } = useAgentPresence(tenantId);
  const workingCount = selectedAgentId ? activeTaskCount(presenceFor(presence?.agents, selectedAgentId)) : 0;

  // Auto-select first agent if none selected
  useEffect(() => {
    if (agents.length > 0 && !selectedAgentId) {
      updateThreadContext({ agentId: agents[0].id });
    }
  }, [agents, selectedAgentId, updateThreadContext]);

  // All sessions for selected agent — poll every 8s so new sessions created
  // elsewhere (another browser, an autonomous agent run, a cron job) show
  // up without a manual refresh. The server already orders by desc(updatedAt)
  // so sessions[0] is always the most recently touched one.
  const { data: allSessions = [], isFetching: sessionsFetching } = useQuery({
    queryKey: ["chat-sessions", selectedAgentId],
    queryFn: () => chatApi.listSessions(selectedAgentId!),
    enabled: !!selectedAgentId,
    refetchInterval: anySessionSending ? false : 8000,
    refetchOnWindowFocus: true,
  });

  const { data: channels = [] } = useQuery({
    queryKey: ["agent-channels", selectedAgentId],
    queryFn: () => agentChannelsApi.list(selectedAgentId!),
    enabled: !!selectedAgentId,
  });

  // Skills assigned to the *current agent*, used to populate the slash-
  // command menu. Previously this fetched every tenant_skills row in the
  // company, which made unrelated skills (e.g. Codie's aztech-entra-groups)
  // appear in other agents' menus and read as "this agent has access" when
  // it didn't. Now we scope to the agent and only surface assignments
  // where both the per-agent flag and the tenant-directory flag are
  // enabled — disabling the underlying tenant skill removes it from
  // every agent's menu in one move.
  const { data: agentSkills = [], isLoading: skillsLoading } = useQuery({
    queryKey: selectedAgentId ? queryKeys.agentSkills.byAgent(selectedAgentId) : ["agent-skills", "none"],
    queryFn: () => skillsApi.listAgentSkills(selectedAgentId as string),
    enabled: !!selectedAgentId && slashMenuOpen,
  });
  const slashMenuSkills = useMemo(
    () => (agentSkills as AgentSkillRow[])
      .filter((s) => s.enabled && s.tenantEnabled)
      .map((skill) => ({
        ...skill,
        disabledReason: slashDisabledSkillReasons.get(skill.name) ?? null,
      })),
    [agentSkills],
  );

  // Filter sessions by active channel
  const sessions = useMemo(() => {
    if (!activeChannelId || activeChannelId === "general") {
      return allSessions.filter((s) => !s.channelId);
    }
    return allSessions.filter((s) => s.channelId === activeChannelId);
  }, [allSessions, activeChannelId]);

  // Draft mode: render the composer with no session behind it. Entered when the
  // user hit "New chat" (?newChat=1) or landed on a channel with no sessions yet.
  // Nothing is persisted until they send — that's when the session row is created.
  const draftMode =
    !activeSessionId && (wantsNewChat || (!sessionsFetching && sessions.length === 0));

  const composerKey = activeSessionId
    ? `session:${activeSessionId}`
    : draftMode && selectedAgentId
      ? `draft:${selectedAgentId}:${activeChannelId ?? "general"}`
      : null;
  const [composerDrafts, setComposerDrafts] = useState<Record<string, ComposerDraft>>(
    () => ({ ...workspaceComposerDraftCache }),
  );
  const currentDraft = composerKey ? composerDrafts[composerKey] : undefined;
  const input = currentDraft?.input ?? "";
  const attachments = currentDraft?.attachments ?? [];

  const updateComposerDraft = useCallback(
    (updater: (draft: ComposerDraft) => ComposerDraft) => {
      if (!composerKey) return;
      setComposerDrafts((prev) => {
        const current = prev[composerKey] ?? { input: "", attachments: [] };
        const nextDraft = updater(current);
        if (!nextDraft.input && nextDraft.attachments.length === 0) {
          delete workspaceComposerDraftCache[composerKey];
          if (!(composerKey in prev)) return prev;
          const next = { ...prev };
          delete next[composerKey];
          return next;
        }
        workspaceComposerDraftCache[composerKey] = nextDraft;
        return { ...prev, [composerKey]: nextDraft };
      });
    },
    [composerKey],
  );

  const setInput = useCallback(
    (next: SetStateAction<string>) => {
      updateComposerDraft((draft) => ({
        ...draft,
        input: typeof next === "function" ? (next as (previous: string) => string)(draft.input) : next,
      }));
    },
    [updateComposerDraft],
  );

  const setAttachments = useCallback(
    (next: SetStateAction<File[]>) => {
      updateComposerDraft((draft) => ({
        ...draft,
        attachments: typeof next === "function" ? (next as (previous: File[]) => File[])(draft.attachments) : next,
      }));
    },
    [updateComposerDraft],
  );

  useEffect(() => {
    if (!selectedAgentId || (!activeSessionId && !draftMode) || sending) return;

    const activeElement = document.activeElement;
    if (
      activeElement instanceof HTMLElement &&
      activeElement !== document.body &&
      activeElement !== inputRef.current
    ) {
      const tagName = activeElement.tagName;
      if (
        tagName === "INPUT" ||
        tagName === "TEXTAREA" ||
        tagName === "SELECT" ||
        activeElement.isContentEditable
      ) {
        return;
      }
    }

    const focusTimer = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(focusTimer);
  }, [selectedAgentId, activeSessionId, activeChannelId, draftMode, sending]);

  useEffect(() => {
    const inputEl = inputRef.current;
    if (!inputEl) return;
    inputEl.style.height = "auto";
    inputEl.style.height = Math.min(inputEl.scrollHeight, 200) + "px";
  }, [composerKey, input]);

  // Auto-select first session when channel changes or no session is selected.
  //
  // Important: while the sessions query is fetching (including background
  // refetches triggered by an invalidation), don't treat an empty list as
  // authoritative — a freshly-created session id may be set on thread
  // context before the list has caught up, and we don't want to nuke it.
  useEffect(() => {
    // While drafting a new chat, don't auto-select an existing session —
    // that would yank the user out of the empty composer they just opened.
    if (wantsNewChat) return;
    if (sessionsFetching) return;
    if (!sessions.length) {
      // Clear session if current channel has no sessions
      if (activeSessionId) {
        const stillValid = sessions.some((s) => s.id === activeSessionId);
        if (!stillValid) {
          updateThreadContext({ sessionId: undefined });
        }
      }
      return;
    }
    // If the current session isn't in the filtered list, select the first one
    const currentInChannel = sessions.some((s) => s.id === activeSessionId);
    if (!currentInChannel) {
      updateThreadContext({ sessionId: sessions[0].id });
    }
  }, [sessions, sessionsFetching, activeSessionId, updateThreadContext, wantsNewChat]);

  // Resolve the active session for the header indicator
  const activeSession = allSessions.find((s) => s.id === activeSessionId);
  const activeSessionBusy = sending || !!activeSession?.isGenerating;
  const sharedSessionBusy = !!activeSession?.isGenerating && !streamingHere;
  const activeChannelName = useMemo(() => {
    if (!activeChannelId || activeChannelId === "general") return "General";
    return channels.find((channel) => channel.id === activeChannelId)?.name
      ?? activeSession?.channelName
      ?? "General";
  }, [activeChannelId, channels, activeSession?.channelName]);

  // Messages for active session — poll every 8s for autonomous agent messages
  // and any updates from other browsers / background jobs. Also refetch on
  // window focus so switching tabs always gives you a fresh view.
  //
  // Only the newest RECENT_MESSAGES_LIMIT rows are fetched and polled. The
  // previous unbounded fetch shipped the entire session history (several MB
  // for monitor channels) and mounted every row in one synchronous commit,
  // freezing the tab. Note the query key differs from agent-chat-tab's
  // ["chat-messages"] — the cached shapes are incompatible.
  const { data: messagesWindow, isLoading: messagesLoading } = useQuery({
    queryKey: ["chat-messages-window", activeSessionId],
    queryFn: () => chatApi.getMessagesWindow(activeSessionId!, { limit: RECENT_MESSAGES_LIMIT }),
    enabled: !!activeSessionId,
    refetchInterval: sending ? false : 8000,
    refetchOnWindowFocus: true,
  });

  const [archive, setArchive] = useState<TranscriptArchive>({
    sessionId: null,
    byId: new Map(),
    hasEarlier: false,
    loadedEarlier: false,
  });
  const [loadingEarlier, setLoadingEarlier] = useState(false);

  // Archive every window row so rows that slide out of the newest-N window
  // never vanish mid-transcript, and reconcile optimistic rows.
  useEffect(() => {
    if (!activeSessionId || !messagesWindow) return;
    setArchive((prev) => {
      const sameSession = prev.sessionId === activeSessionId;
      const byId = sameSession ? new Map(prev.byId) : new Map<string, ChatMessage>();
      const incomingIds = new Set(messagesWindow.messages.map((m) => m.id));
      for (const m of messagesWindow.messages) byId.set(m.id, m);
      // Drop optimistic user rows (seeded into the query cache after a
      // completed stream with a fake "opt-user-*" id) once the poll delivers
      // the real persisted row — otherwise the archive would keep rendering
      // the message twice. The 60s fallback catches any content mismatch.
      for (const [id, row] of byId) {
        if (!id.startsWith("opt-user-") || incomingIds.has(id)) continue;
        const persisted = messagesWindow.messages.some(
          (m) => m.role === "user" && m.content === row.content,
        );
        const stale = Date.now() - new Date(row.createdAt).getTime() > 60_000;
        if (persisted || stale) byId.delete(id);
      }
      return {
        sessionId: activeSessionId,
        byId,
        // The live window's hasMore only describes rows older than the window
        // itself; once earlier pages are loaded, paging owns the flag.
        hasEarlier: sameSession && prev.loadedEarlier ? prev.hasEarlier : messagesWindow.hasMore,
        loadedEarlier: sameSession ? prev.loadedEarlier : false,
      };
    });
  }, [messagesWindow, activeSessionId]);

  // The rendered transcript: live window merged with archived rows. Window
  // rows win on id collisions (they're fresher — the sweeper rewrites rows
  // in place). Sorted by (createdAt, id) to mirror the server's ordering.
  const messages = useMemo(() => {
    const windowRows = messagesWindow?.messages ?? [];
    if (archive.sessionId !== activeSessionId) return windowRows;
    if (archive.byId.size === 0) return windowRows;
    const ids = new Set(windowRows.map((m) => m.id));
    const merged = [...windowRows];
    for (const [id, m] of archive.byId) {
      if (!ids.has(id)) merged.push(m);
    }
    merged.sort((a, b) => {
      const t = a.createdAt.localeCompare(b.createdAt);
      return t !== 0 ? t : a.id.localeCompare(b.id);
    });
    return merged;
  }, [messagesWindow, archive, activeSessionId]);

  // A newly-created session starts fetching as soon as its id enters the URL.
  // The POST persists the user row before the assistant stream starts, so that
  // first fetch can contain the real user message while activeRun still renders
  // its optimistic copy. Treat the latest non-streaming turn row as the source
  // of truth and hide the optimistic copy once that exact row is present.
  const optimisticUserIsPersisted = useMemo(() => {
    if (!optimisticUserMsg) return false;
    const baselineIndex = optimisticAfterMessageId
      ? messages.findIndex((message) => message.id === optimisticAfterMessageId)
      : -1;
    // A missing non-null baseline means the transcript window changed under
    // us. Keep the optimistic row until the server response is authoritative.
    if (optimisticAfterMessageId && baselineIndex === -1) return false;
    for (let i = messages.length - 1; i > baselineIndex; i--) {
      const message = messages[i];
      if (message.role === "assistant" && message.metadata?.streaming) continue;
      return message.role === "user" && message.content === optimisticUserMsg;
    }
    return false;
  }, [messages, optimisticAfterMessageId, optimisticUserMsg]);

  const hasEarlier =
    archive.sessionId === activeSessionId ? archive.hasEarlier : (messagesWindow?.hasMore ?? false);

  // Scroll-anchor bookkeeping for prepended history (see the layout effect).
  const prependAnchorRef = useRef<{ prevScrollHeight: number; prevScrollTop: number } | null>(null);

  const handleLoadEarlier = useCallback(async () => {
    if (!activeSessionId || loadingEarlier) return;
    const oldest = messages[0];
    if (!oldest) return;
    setLoadingEarlier(true);
    // Record the viewport before the rows land so the layout effect can keep
    // what the user is reading fixed in place while history grows above it.
    const el = messagesScrollRef.current;
    if (el) {
      prependAnchorRef.current = { prevScrollHeight: el.scrollHeight, prevScrollTop: el.scrollTop };
    }
    try {
      const page = await chatApi.getMessagesWindow(activeSessionId, {
        limit: RECENT_MESSAGES_LIMIT,
        beforeId: oldest.id,
      });
      setArchive((prev) => {
        if (prev.sessionId !== activeSessionId) return prev;
        const byId = new Map(prev.byId);
        for (const m of page.messages) {
          if (!byId.has(m.id)) byId.set(m.id, m);
        }
        return { ...prev, byId, hasEarlier: page.hasMore, loadedEarlier: true };
      });
    } catch (err) {
      console.error("Failed to load earlier messages:", err);
      prependAnchorRef.current = null;
    } finally {
      setLoadingEarlier(false);
    }
  }, [activeSessionId, loadingEarlier, messages]);

  const latestMessage = messages[messages.length - 1];
  const latestMessageReadSignal = latestMessage
    ? `${latestMessage.id}:${latestMessage.metadata?.streaming === true ? "streaming" : latestMessage.metadata?.completedAt ?? "complete"}`
    : null;

  // Opening a session clears its unread marker immediately. Mark it read again
  // when a new message appears while it is open so a completed response never
  // leaves a blue dot on the chat the user is already viewing.
  useEffect(() => {
    if (!activeSessionId) return;
    queryClient.setQueryData<ChatSession[]>(
      ["chat-sessions", selectedAgentId],
      (current) => current?.map((session) =>
        session.id === activeSessionId ? { ...session, hasUnread: false } : session,
      ),
    );

    void chatNotificationsApi.markSessionRead(activeSessionId)
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ["chat-notifications", tenantId] });
        queryClient.invalidateQueries({ queryKey: ["chat-sessions", selectedAgentId] });
      })
      .catch(() => {});
  }, [activeSessionId, latestMessageReadSignal, selectedAgentId, tenantId, queryClient]);

  /**
   * Latest input-token reading for the current session.
   *  1. If a `usage` SSE event landed for this session, prefer it (it's
   *     fresher than any polled DB read).
   *  2. Otherwise, take the most recent assistant message that carries
   *     `metadata.usage.input_tokens`.
   * Returns null when no turn has reported usage yet (e.g. fresh session
   * or pre-fix messages); the donut is hidden in that case.
   */
  const contextUsage = useMemo(() => {
    if (liveUsage && activeSessionId && liveUsage.sessionId === activeSessionId) {
      return { tokens: liveUsage.inputTokens, model: liveUsage.model };
    }
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.role !== "assistant") continue;
      const tokens = m.metadata?.usage?.input_tokens;
      if (typeof tokens === "number" && tokens > 0) {
        return { tokens, model: m.metadata?.usageModel ?? null };
      }
    }
    return null;
  }, [activeSessionId, liveUsage, messages]);

  // Scroll behaviour:
  // - When opening a session for the first time (or switching sessions), jump
  //   instantly to the bottom before paint so the user never sees the top of
  //   the conversation flash into view.
  // - For subsequent new messages / streaming content within the same session,
  //   scroll smoothly so the user can see the new content arrive.
  const prevSessionIdRef = useRef<string | null>(null);
  // Whether the user is pinned near the bottom. When they scroll up we stop
  // auto-following new/streaming content so we don't yank them back down.
  const atBottomRef = useRef(true);
  const lastMessageId = messages.length > 0 ? messages[messages.length - 1].id : null;
  useLayoutEffect(() => {
    const isSessionChange = prevSessionIdRef.current !== activeSessionId;
    prevSessionIdRef.current = activeSessionId;
    // A fresh session always starts pinned to the bottom, so hide the button
    // until the user actually scrolls up.
    if (isSessionChange) {
      setShowScrollButton(false);
      atBottomRef.current = true;
    }

    // Older history was just prepended — keep the row the user was reading
    // fixed in place instead of snapping to the bottom.
    if (prependAnchorRef.current) {
      const el = messagesScrollRef.current;
      if (el) {
        const { prevScrollHeight, prevScrollTop } = prependAnchorRef.current;
        el.scrollTop = el.scrollHeight - prevScrollHeight + prevScrollTop;
      }
      prependAnchorRef.current = null;
      return;
    }

    // Only auto-scroll when the user is already at the bottom (or just switched
    // sessions). If they've scrolled up to read — including during a streaming
    // turn — leave their position alone; the "jump to latest" button covers it.
    if (!isSessionChange && !atBottomRef.current) return;

    messagesEndRef.current?.scrollIntoView({
      // Streaming content updates many times per second. Starting a new smooth
      // animation for every chunk makes the transcript move up and down. Keep
      // the pinned bottom exact during a stream; smooth movement is only for a
      // completed/new persisted message.
      behavior: isSessionChange || streamingHere ? "auto" : "smooth",
      block: "end",
    });
  }, [lastMessageId, messages.length, streamingContent, optimisticUserMsg, activeSessionId, streamingHere]);

  // Toggle the "jump to latest" affordance based on how far the user has
  // scrolled up. ~120px of slack keeps it hidden while sitting at the bottom.
  const handleMessagesScroll = useCallback(() => {
    const el = messagesScrollRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    atBottomRef.current = distanceFromBottom <= 120;
    setShowScrollButton(distanceFromBottom > 120);
  }, []);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, []);

  // Close slash menu on outside click or Escape
  useEffect(() => {
    if (!slashMenuOpen) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (slashMenuOpen && slashMenuRef.current && !slashMenuRef.current.contains(target)) {
        setSlashMenuOpen(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setSlashMenuOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [slashMenuOpen]);

  // Create session — uses active channel if set.
  //
  // IMPORTANT: seed the sessions query cache with the new session *before*
  // setting the active session id, otherwise the "auto-select first session"
  // effect below fires on the next render with `sessions = []` (the
  // invalidated query hasn't refetched yet), decides the new session id is
  // invalid, and wipes it. This bug was invisible for agents with prior
  // sessions and only triggered for brand-new agents / sub-agents — clicking
  // "New chat" appeared to do nothing because the session id was set and
  // immediately cleared in the same render cycle.
  const [newChatError, setNewChatError] = useState<string | null>(null);
  const createSession = useMutation({
    mutationFn: (channelId?: string) => {
      if (!selectedAgentId) {
        return Promise.reject(new Error("No agent selected"));
      }
      const ch = channelId ?? (activeChannelId && activeChannelId !== "general" ? activeChannelId : undefined);
      return chatApi.createSession(tenantId, selectedAgentId, ch);
    },
    onSuccess: (session) => {
      // Seed the cache optimistically so the new session is visible on the
      // very next render, then invalidate to pick up any server-side fields
      // we don't know about yet.
      queryClient.setQueryData<ChatSession[]>(
        ["chat-sessions", selectedAgentId],
        (prev) => {
          if (!prev) return [session];
          if (prev.some((s) => s.id === session.id)) return prev;
          return [session, ...prev];
        },
      );
      queryClient.invalidateQueries({ queryKey: ["chat-sessions", selectedAgentId] });
      updateThreadContext({ sessionId: session.id });
      setNewChatError(null);
    },
    onError: (err: unknown) => {
      // Surface any failure to the user so we don't silently leave them
      // staring at an unchanged empty state. Previously these errors were
      // swallowed — a server-side rejection looked identical to "button did
      // nothing".
      const msg = err instanceof Error ? err.message : String(err);
      console.error("Failed to create chat session:", err);
      setNewChatError(msg || "Could not start a new chat. Please try again.");
    },
  });

  // Note: the session for a "New chat" is NOT created here. Opening New chat
  // only puts the surface into draft mode (see `draftMode`); the row is created
  // lazily on the first send in handleSend, so recents never fills with empty
  // "New Chat" sessions the user never wrote in.

  // @Mention candidates: other active agents in the company plus files
  // from the current agent's workspace, merged into one dropdown.
  const workspaceFiles = useWorkspaceFileMentions(selectedAgentId, !!mentionState?.active);
  const mentionCandidates: MentionCandidate[] = mentionState?.active
    ? buildMentionCandidates(
        agents.filter((a) => a.id !== selectedAgentId && a.status === "active"),
        workspaceFiles,
        mentionState.query,
      )
    : [];

  // Insert a selected mention into the textarea. Agents insert as
  // "@Name " (drives send-time routing); files insert as a plain workspace
  // path the agent can read.
  const insertMention = useCallback(
    (candidate: MentionCandidate) => {
      if (!mentionState) return;
      const before = input.slice(0, mentionState.startIndex);
      const after = input.slice(mentionState.startIndex + mentionState.query.length + 1);
      const inserted = candidate.kind === "agent"
        ? `@${candidate.name} `
        : fileMentionText(candidate.path);
      setInput(`${before}${inserted}${after}`);
      setMentionState(null);
      inputRef.current?.focus();
    },
    [input, mentionState, setInput],
  );

  // Auto-resize textarea + detect @mentions
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart ?? value.length;
    setInput(value);

    const textBeforeCursor = value.slice(0, cursorPos);
    // Dots and slashes keep the mention alive while typing a filename
    // (e.g. "@reports/q1.csv"); agent names never contain them so agent
    // matching is unaffected.
    const atMatch = textBeforeCursor.match(/@([A-Za-z0-9 ._/-]*)$/);
    if (atMatch) {
      setMentionState({
        active: true,
        query: atMatch[1],
        startIndex: cursorPos - atMatch[0].length,
        selectedIndex: 0,
      });
    } else {
      setMentionState(null);
    }

    const el = e.target;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 200) + "px";
  }, [setInput]);

  const updateActiveRun = useCallback((sessionId: string, updater: (run: ActiveChatRun) => ActiveChatRun) => {
    setActiveRuns((prev) => {
      const current = prev[sessionId];
      if (!current) return prev;
      return { ...prev, [sessionId]: updater(current) };
    });
  }, []);

  const handleStop = useCallback(() => {
    if (!activeSessionId) return;
    abortControllersRef.current[activeSessionId]?.abort();
  }, [activeSessionId]);

  // Stable identity so memoized MessageBubbles don't re-render on every
  // parent render (polls, stream timer ticks, composer keystrokes).
  const handleCopyMenu = useCallback((e: React.MouseEvent<HTMLDivElement>, content: string) => {
    e.preventDefault();
    setCopyMenu({
      x: e.clientX,
      y: e.clientY,
      content,
      selection: window.getSelection()?.toString() ?? "",
    });
  }, []);

  const handleFileSelect = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    setAttachments((prev) => [...prev, ...files].slice(0, 5));
    e.target.value = "";
  }, [setAttachments]);

  // ── Upload skill ──────────────────────────────────────────────
  // Separate file picker from the regular attachments — a SKILL.md
  // upload is a registration action, not a chat attachment. Reads the
  // file as text, posts to the upload endpoint, and refreshes the
  // tenant + agent skills queries so the slash menu picks it up.
  const skillFileInputRef = useRef<HTMLInputElement>(null);
  const [skillUploadState, setSkillUploadState] = useState<
    | { kind: "idle" }
    | { kind: "uploading"; filename: string }
    | { kind: "success"; name: string; created: boolean }
    | { kind: "error"; message: string }
  >({ kind: "idle" });

  const uploadSkillMutation = useMutation({
    mutationFn: async ({ file }: { file: File }) => {
      const content = await file.text();
      if (!selectedAgentId) throw new Error("No agent selected");
      // Strip extension; the server slugifies but a sensible default helps if
      // the user uploaded a file without YAML frontmatter.
      const fallbackName = file.name.replace(/\.(md|markdown|txt)$/i, "");
      return skillsApi.uploadAgentSkill(selectedAgentId, {
        name: fallbackName,
        content,
      });
    },
    onSuccess: (result) => {
      setSkillUploadState({
        kind: "success",
        name: result.name,
        created: result.created,
      });
      // Refresh both lists so the slash menu, agent Config tab and
      // tenant Skills directory all reflect the newly registered skill.
      queryClient.invalidateQueries({ queryKey: queryKeys.tenantSkills.byCompany(tenantId) });
      if (selectedAgentId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.agentSkills.byAgent(selectedAgentId) });
      }
      // Auto-clear the success banner after a few seconds.
      setTimeout(() => {
        setSkillUploadState((s) => (s.kind === "success" ? { kind: "idle" } : s));
      }, 4000);
    },
    onError: (err) => {
      const message = err instanceof Error ? err.message : String(err);
      setSkillUploadState({ kind: "error", message });
      setTimeout(() => {
        setSkillUploadState((s) => (s.kind === "error" ? { kind: "idle" } : s));
      }, 6000);
    },
  });

  const handleSkillFileSelect = useCallback(() => {
    if (!selectedAgentId) {
      setSkillUploadState({ kind: "error", message: "Select an agent first." });
      return;
    }
    skillFileInputRef.current?.click();
  }, [selectedAgentId]);

  const handleSkillFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = "";
      if (!file) return;
      if (file.size > 200_000) {
        setSkillUploadState({ kind: "error", message: "Skill file exceeds 200KB." });
        return;
      }
      setSkillUploadState({ kind: "uploading", filename: file.name });
      uploadSkillMutation.mutate({ file });
    },
    [uploadSkillMutation],
  );

  const removeAttachment = useCallback((index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  }, [setAttachments]);

  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = Array.from(e.clipboardData.items);
    const imageItems = items.filter((item) => item.type.startsWith("image/"));
    if (imageItems.length === 0) return; // let default text paste happen

    e.preventDefault();
    const files = imageItems
      .map((item) => item.getAsFile())
      .filter((f): f is File => f !== null);
    if (files.length > 0) {
      setAttachments((prev) => [...prev, ...files].slice(0, 5));
    }
  }, [setAttachments]);

  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files).filter(
      (f) => f.type.startsWith("image/") || f.type === "application/pdf" ||
        f.type.startsWith("text/") || f.name.match(/\.(json|csv|doc|docx|xls|xlsx)$/i),
    );
    if (files.length > 0) {
      setAttachments((prev) => [...prev, ...files].slice(0, 5));
    }
  }, [setAttachments]);

  // Guards handleSend against re-entry across the async session-creation await.
  const sendInFlightRef = useRef<Set<string>>(new Set());

  // Send message with streaming
  const handleSend = async () => {
    if (activeSessionBusy || (!input.trim() && attachments.length === 0)) return;
    const initialSendKey = activeSessionId ?? composerKey;
    if (!initialSendKey) return;
    // Synchronous re-entrancy guard. `sending` is async state, so it can't
    // stop a second call that arrives before React commits — important now
    // that a draft send awaits session creation, which would otherwise let a
    // fast double-Enter create two sessions.
    if (sendInFlightRef.current.has(initialSendKey)) return;
    sendInFlightRef.current.add(initialSendKey);

    // Lazy session creation: in draft mode there is no row yet, so create one
    // now (on the user's first message) and use its id for the rest of the send.
    // This is the only place a "New chat" turns into a real session.
    let sessionId = activeSessionId;
    if (!sessionId) {
      try {
        const created = await createSession.mutateAsync(undefined);
        sessionId = created.id;
        sendInFlightRef.current.delete(initialSendKey);
        if (sendInFlightRef.current.has(sessionId)) return;
        sendInFlightRef.current.add(sessionId);
      } catch {
        // createSession.onError already surfaces the failure to the user.
        sendInFlightRef.current.delete(initialSendKey);
        return;
      }
      // Drop the draft flag now that the chat is real.
      if (wantsNewChat) {
        setSearchParams((p) => { p.delete("newChat"); return p; }, { replace: true });
      }
    }
    if (!sessionId) {
      sendInFlightRef.current.delete(initialSendKey);
      return;
    }

    const msg = input.trim();
    const turnBaselineMessageId = messages[messages.length - 1]?.id ?? null;
    const filesToSend = [...attachments];
    const mentionMatch = msg.match(/(?:^|\s)@([A-Za-z][A-Za-z0-9 _-]*)/);
    const mentionedAgent = mentionMatch
      ? agents.find((a) => a.name.toLowerCase() === mentionMatch[1].trim().toLowerCase() && a.id !== selectedAgentId)
      : null;

    // Sending your own message always jumps you to the bottom, even if you'd
    // scrolled up while reading.
    atBottomRef.current = true;
    setInput("");
    setAttachments([]);
    setMentionState(null);
    setActiveRuns((prev) => ({
      ...prev,
      [sessionId]: {
        optimisticUserMsg: msg || `[${filesToSend.length} file(s)]`,
        optimisticAfterMessageId: turnBaselineMessageId,
        streamingContent: "",
        activityEvents: [],
        streamingAgent: mentionedAgent ? { name: mentionedAgent.name, avatarUrl: mentionedAgent.avatarUrl } : null,
      },
    }));
    queryClient.setQueryData<ChatSession[]>(
      ["chat-sessions", selectedAgentId],
      (current) => current?.map((session) =>
        session.id === sessionId
          ? { ...session, isGenerating: true, hasUnread: false }
          : session,
      ),
    );
    if (inputRef.current) {
      inputRef.current.style.height = "auto";
    }

    const controller = new AbortController();
    abortControllersRef.current[sessionId] = controller;
    let accumulatedContent = "";
    // Mirror of the streamed tool events, kept in a plain map so the finally
    // block can attach the completed trace to the optimistic assistant message
    // (the React state is cleared on teardown). Keyed by toolUseId.
    const toolEventsLocal = new Map<string, ActivityEvent>();
    let doneMessageId: string | null = null;
    let doneRespondingAgentId: string | null = null;
    let doneRespondingAgentName: string | undefined;
    let doneRespondingAgentAvatarUrl: string | null | undefined;
    let aborted = false;

    try {
      let body: BodyInit;
      const headers: Record<string, string> = {};

      if (filesToSend.length > 0) {
        const form = new FormData();
        form.append("content", msg || "(see attached files)");
        for (const file of filesToSend) {
          form.append("files", file);
        }
        body = form;
      } else {
        headers["Content-Type"] = "application/json";
        body = JSON.stringify({ content: msg });
      }

      const res = await fetch(`/api/chat/sessions/${sessionId}/messages?stream=true`, {
        method: "POST",
        credentials: "include",
        headers,
        body,
        signal: controller.signal,
      });

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      const reader = res.body?.getReader();
      if (!reader) throw new Error("No response body");

      const decoder = new TextDecoder();
      let buffer = "";
      let flushTimer: ReturnType<typeof setTimeout> | null = null;
      const flushStreamingContent = () => {
        updateActiveRun(sessionId, (run) => ({ ...run, streamingContent: accumulatedContent }));
      };
      const processStreamLine = (line: string) => {
        const trimmed = line.trim();
        if (!trimmed || !trimmed.startsWith("data: ")) return;
        const data = trimmed.slice(6);
        if (data === "[DONE]") return;

        try {
          const parsed = JSON.parse(data);

          if (parsed.type === "done") {
            if (parsed.assistantMessageId) {
              doneMessageId = parsed.assistantMessageId;
            }
            if (parsed.respondingAgentId) {
              doneRespondingAgentId = parsed.respondingAgentId;
              doneRespondingAgentName = parsed.respondingAgentName;
              doneRespondingAgentAvatarUrl = parsed.respondingAgentAvatarUrl;
            }
            return;
          }

          if (parsed.type === "usage" && parsed.usage) {
            const inputTokens = Number(parsed.usage.input_tokens ?? 0);
            if (Number.isFinite(inputTokens) && inputTokens > 0 && sessionId) {
              setLiveUsage({
                sessionId,
                inputTokens,
                model: typeof parsed.model === "string" ? parsed.model : null,
              });
            }
            return;
          }

          if (parsed.type === "tool_call" && parsed.name) {
            const name = String(parsed.name);
            // Skip the synthetic "Working" sentinel OpenClaw agents send
            // - that's just a placeholder until the first text token arrives.
            if (name !== "Working" && !name.startsWith("Working (")) {
              const status = (parsed.status as ActivityEvent["status"] | undefined) ?? "started";
              const toolUseId = String(parsed.toolUseId ?? `${name}:${parsed.timestamp ?? Date.now()}`);
              updateActiveRun(sessionId, (run) => {
                const prev = run.activityEvents;
                const idx = prev.findIndex((e) => e.toolUseId === toolUseId);
                const incoming: ActivityEvent = {
                  toolUseId,
                  name,
                  status,
                  args: typeof parsed.args === "string" ? parsed.args : prev[idx]?.args,
                  durationMs: typeof parsed.durationMs === "number" ? parsed.durationMs : prev[idx]?.durationMs,
                  error: typeof parsed.error === "string" ? parsed.error : prev[idx]?.error,
                  resultPreview: typeof parsed.resultPreview === "string" ? parsed.resultPreview : prev[idx]?.resultPreview,
                  timestamp: typeof parsed.timestamp === "number" ? parsed.timestamp : Date.now(),
                };
                if (idx === -1) return { ...run, activityEvents: [...prev, incoming] };
                const next = prev.slice();
                next[idx] = { ...prev[idx], ...incoming };
                return { ...run, activityEvents: next };
              });
              // Mirror into the plain map (merging onto the same id) so the
              // finished trace can be attached to the persisted message.
              const prevLocal = toolEventsLocal.get(toolUseId);
              toolEventsLocal.set(toolUseId, {
                toolUseId,
                name,
                status,
                args: typeof parsed.args === "string" ? parsed.args : prevLocal?.args,
                durationMs: typeof parsed.durationMs === "number" ? parsed.durationMs : prevLocal?.durationMs,
                error: typeof parsed.error === "string" ? parsed.error : prevLocal?.error,
                resultPreview: typeof parsed.resultPreview === "string" ? parsed.resultPreview : prevLocal?.resultPreview,
                timestamp: typeof parsed.timestamp === "number" ? parsed.timestamp : (prevLocal?.timestamp ?? Date.now()),
              });
            }
            return;
          }

          const token = parsed.choices?.[0]?.delta?.content;
          if (token) {
            accumulatedContent += token;
            if (!flushTimer) {
              flushTimer = setTimeout(() => {
                flushStreamingContent();
                flushTimer = null;
              }, 50);
            }
          }
        } catch {
          // Not JSON, skip
        }
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed || !trimmed.startsWith("data: ")) continue;
          const data = trimmed.slice(6);
          if (data === "[DONE]") continue;

          try {
            const parsed = JSON.parse(data);

            if (parsed.type === "done") {
              if (parsed.assistantMessageId) {
                doneMessageId = parsed.assistantMessageId;
              }
              if (parsed.respondingAgentId) {
                doneRespondingAgentId = parsed.respondingAgentId;
                doneRespondingAgentName = parsed.respondingAgentName;
                doneRespondingAgentAvatarUrl = parsed.respondingAgentAvatarUrl;
              }
              continue;
            }

            if (parsed.type === "usage" && parsed.usage) {
              const inputTokens = Number(parsed.usage.input_tokens ?? 0);
              if (Number.isFinite(inputTokens) && inputTokens > 0 && sessionId) {
                setLiveUsage({
                  sessionId,
                  inputTokens,
                  model: typeof parsed.model === "string" ? parsed.model : null,
                });
              }
              continue;
            }

            if (parsed.type === "tool_call" && parsed.name) {
              const name = String(parsed.name);
              // Skip the synthetic "Working" sentinel OpenClaw agents send
              // — that's just a placeholder until the first text token arrives.
              if (name !== "Working" && !name.startsWith("Working (")) {
                const status = (parsed.status as ActivityEvent["status"] | undefined) ?? "started";
                const toolUseId = String(parsed.toolUseId ?? `${name}:${parsed.timestamp ?? Date.now()}`);
                updateActiveRun(sessionId, (run) => {
                  const prev = run.activityEvents;
                  const idx = prev.findIndex((e) => e.toolUseId === toolUseId);
                  const incoming: ActivityEvent = {
                    toolUseId,
                    name,
                    status,
                    args: typeof parsed.args === "string" ? parsed.args : prev[idx]?.args,
                    durationMs: typeof parsed.durationMs === "number" ? parsed.durationMs : prev[idx]?.durationMs,
                    error: typeof parsed.error === "string" ? parsed.error : prev[idx]?.error,
                    resultPreview: typeof parsed.resultPreview === "string" ? parsed.resultPreview : prev[idx]?.resultPreview,
                    timestamp: typeof parsed.timestamp === "number" ? parsed.timestamp : Date.now(),
                  };
                  if (idx === -1) return { ...run, activityEvents: [...prev, incoming] };
                  const next = prev.slice();
                  next[idx] = { ...prev[idx], ...incoming };
                  return { ...run, activityEvents: next };
                });
                // Mirror into the plain map (merging onto the same id) so the
                // finished trace can be attached to the persisted message.
                const prevLocal = toolEventsLocal.get(toolUseId);
                toolEventsLocal.set(toolUseId, {
                  toolUseId,
                  name,
                  status,
                  args: typeof parsed.args === "string" ? parsed.args : prevLocal?.args,
                  durationMs: typeof parsed.durationMs === "number" ? parsed.durationMs : prevLocal?.durationMs,
                  error: typeof parsed.error === "string" ? parsed.error : prevLocal?.error,
                  resultPreview: typeof parsed.resultPreview === "string" ? parsed.resultPreview : prevLocal?.resultPreview,
                  timestamp: typeof parsed.timestamp === "number" ? parsed.timestamp : (prevLocal?.timestamp ?? Date.now()),
                });
              }
              continue;
            }

            const token = parsed.choices?.[0]?.delta?.content;
            if (token) {
              accumulatedContent += token;
              if (!flushTimer) {
                flushTimer = setTimeout(() => {
                  flushStreamingContent();
                  flushTimer = null;
                }, 50);
              }
            }
          } catch {
            // Not JSON, skip
          }
        }
      }
      buffer += decoder.decode();
      if (buffer.trim()) {
        for (const line of buffer.split("\n")) {
          processStreamLine(line);
        }
      }
      if (flushTimer) clearTimeout(flushTimer);
      if (accumulatedContent) {
        flushStreamingContent();
      }
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === "AbortError") {
        aborted = true;
      } else {
        console.error("Chat stream error:", err);
      }
    } finally {
      delete abortControllersRef.current[sessionId];
      sendInFlightRef.current.delete(initialSendKey);
      sendInFlightRef.current.delete(sessionId);
      setActiveRuns((prev) => {
        if (!(sessionId in prev)) return prev;
        const next = { ...prev };
        delete next[sessionId];
        return next;
      });
      queryClient.setQueryData<ChatSession[]>(
        ["chat-sessions", selectedAgentId],
        (current) => current?.map((session) =>
          session.id === sessionId ? { ...session, isGenerating: false } : session,
        ),
      );

      if (aborted || !doneMessageId || !accumulatedContent) {
        queryClient.invalidateQueries({ queryKey: ["chat-messages-window", sessionId] });
        queryClient.invalidateQueries({ queryKey: ["chat-sessions", selectedAgentId] });
      } else if (doneMessageId && accumulatedContent) {
        // Attach the tool trace so the just-completed bubble keeps its "N steps"
        // section immediately (the server persists the same steps on the row;
        // the next poll reconciles by id). Map the live shape → persisted shape
        // (resultPreview → result) and treat a lingering "started" as completed.
        const toolSteps = Array.from(toolEventsLocal.values()).map((e) => ({
          toolUseId: e.toolUseId,
          name: e.name,
          status: e.status === "started" ? ("completed" as const) : e.status,
          args: e.args,
          result: e.resultPreview,
          error: e.error,
          durationMs: e.durationMs,
          timestamp: e.timestamp,
        }));
        const assistantMeta: ChatMessage["metadata"] =
          doneRespondingAgentId || toolSteps.length
            ? {
                ...(doneRespondingAgentId
                  ? { respondingAgentName: doneRespondingAgentName, respondingAgentAvatarUrl: doneRespondingAgentAvatarUrl }
                  : {}),
                ...(toolSteps.length ? { toolSteps } : {}),
              }
            : null;
        queryClient.setQueryData<ChatMessagesWindow>(
          ["chat-messages-window", sessionId],
          (old) => {
            // Idempotent seed: drop any prior optimistic placeholder + this
            // turn's assistant row so re-runs don't stack up.
            const base = (old?.messages ?? []).filter(
              (m) => !m.id.startsWith("opt-user-") && m.id !== doneMessageId,
            );
            const seeded: ChatMessage[] = [];
            // Only add a placeholder user row if the authoritative one isn't
            // already present (a mid-stream refetch may have added it) — this
            // is what caused the first message to duplicate then vanish.
            const baselineIndex = turnBaselineMessageId
              ? base.findIndex((m) => m.id === turnBaselineMessageId)
              : -1;
            const latestTurnRow = base.slice(baselineIndex + 1).reverse().find(
              (m) => !(m.role === "assistant" && m.metadata?.streaming),
            );
            const hasRealUser = latestTurnRow?.role === "user" && latestTurnRow.content === msg;
            const existingAssistant = old?.messages.find((m) => m.id === doneMessageId);
            const assistantCreatedAt = existingAssistant?.createdAt ?? new Date().toISOString();
            if (!hasRealUser) {
              // Use the server assistant row's timestamp as the ordering anchor.
              // A one-millisecond offset keeps the temporary user row before its
              // assistant even when both would otherwise share the same clock tick.
              const userCreatedAt = new Date(new Date(assistantCreatedAt).getTime() - 1).toISOString();
              seeded.push({ id: `opt-user-${Date.now()}`, sessionId: sessionId!, role: "user" as const, content: msg, respondingAgentId: null, metadata: null, createdAt: userCreatedAt });
            }
            seeded.push({ id: doneMessageId!, sessionId: sessionId!, role: "assistant" as const, content: accumulatedContent, respondingAgentId: doneRespondingAgentId, metadata: assistantMeta, createdAt: assistantCreatedAt });
            return { hasMore: old?.hasMore ?? false, messages: [...base, ...seeded] };
          },
        );
        // Reconcile against the server so the placeholder user row (if any) is
        // replaced by the authoritative row instead of lingering as a duplicate.
        queryClient.invalidateQueries({ queryKey: ["chat-messages-window", sessionId] });
        queryClient.invalidateQueries({ queryKey: ["chat-sessions", selectedAgentId] });
      }
      if (activeSessionIdRef.current === sessionId) {
        inputRef.current?.focus();
      }
    }
  };

  return (
    <ArtifactProvider>
    <div className="flex h-full w-full min-w-0 flex-1 overflow-hidden">
    <div className="flex h-full w-full min-w-0 flex-1 flex-col bg-background overflow-hidden">
      {!selectedAgentId ? (
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center max-w-md">
            <Bot size={40} className="mx-auto mb-3 text-text-muted" />
            <p className="text-sm text-text-secondary">Select an agent from the sidebar to start chatting</p>
          </div>
        </div>
      ) : (!activeSessionId && !draftMode) ? (
        // Transient: an agent is picked but the session list hasn't resolved a
        // selection yet. Draft mode (New chat / empty channel) skips this and
        // renders the composer directly below.
        <div className="flex flex-1 items-center justify-center">
          <Loader2 size={20} className="animate-spin text-text-muted" />
        </div>
      ) : (
        <>
          {/* Channel/agent indicator bar — tappable on mobile to open channel picker */}
          <div className="flex min-h-14 shrink-0 items-center gap-2 border-b border-[#f0ede8] bg-white px-6 py-2 lg:px-10 max-sm:px-3 dark:border-border dark:bg-background">
            <button
              type="button"
              onClick={() => mobileChannelSheet?.openChannelSheet()}
              disabled={!mobileChannelSheet}
              className="flex-1 min-w-0 flex items-center gap-2 text-left sm:cursor-default max-sm:hover:bg-surface-hover transition-colors -mx-1 px-1 py-0.5 rounded"
            >
              {selectedAgent && (
                <AgentAvatar agent={selectedAgent} size={18} ringClassName="border-background" />
              )}
              <span className="text-[14px] font-medium text-text-primary truncate">
                {selectedAgent?.name ?? activeChannelName}
              </span>
              <span className="text-[12px] text-text-muted truncate">
                # {activeChannelName}
              </span>
              {mobileChannelSheet && (
                <ChevronDown size={12} className="text-text-muted shrink-0 sm:hidden" />
              )}
            </button>
            {workingCount > 0 && (
              <span className="hidden flex-none items-center gap-1.5 text-[11px] font-medium text-[#10a37f] sm:inline-flex">
                <span className="h-1.5 w-1.5 rounded-full bg-[#10a37f]" />
                Working on {workingCount} {workingCount === 1 ? "task" : "tasks"}
              </span>
            )}
            {contextUsage && (
              <ContextDonut
                tokens={contextUsage.tokens}
                max={resolveContextWindow(
                  contextUsage.model
                    ?? (selectedAgent?.adapterConfig?.model as string | undefined)
                    ?? null,
                )}
              />
            )}
          </div>

          {/* Messages — full width with centered content */}
          <div ref={messagesScrollRef} onScroll={handleMessagesScroll} className="flex-1 overflow-y-auto overflow-x-hidden chat-scroll px-6 py-4 lg:px-10 max-sm:px-3">
            {/* Copy affordances: floating pill over highlighted text + right-click menu */}
            <SelectionCopyChip containerRef={messagesScrollRef} />
            <MessageContextMenu menu={copyMenu} onClose={() => setCopyMenu(null)} />
            {messages.length === 0 && !(streamingHere && optimisticUserMsg) && (
              <div className="flex h-full items-center justify-center">
                {draftMode ? (
                  // The one start screen (the separate dashboard starter page was
                  // folded in): the welcome (greeting + the company's name, as
                  // the old start page had it), who you're talking to, and
                  // starters that fill the box for you to send.
                  <div className="text-center max-w-xl">
                    <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-accent to-accent-hover">
                      <Sparkles size={28} className="text-white" />
                    </div>
                    <h2 className="text-[22px] font-semibold text-text-primary mb-1">
                      Good {timeOfDayGreeting()}, {firstName(authUser)}
                    </h2>
                    <p className="text-sm text-text-muted">
                      What would you like to do today at {tenantName}?
                    </p>
                    <p className="mt-1.5 text-[12.5px] text-text-muted/80">
                      How can {selectedAgent?.name ?? "I"} help you today?
                      {selectedAgent && agentRoleLabel(selectedAgent) && (
                        <> · {agentRoleLabel(selectedAgent)}</>
                      )}
                    </p>
                    {selectedAgent && (
                      <div className="mt-5 flex flex-wrap justify-center gap-2">
                        {CHAT_STARTER_SUGGESTIONS.map((suggestion) => (
                          <button
                            key={suggestion}
                            type="button"
                            onClick={() => {
                              setInput(suggestion);
                              inputRef.current?.focus();
                            }}
                            className="rounded-full border border-border bg-surface px-3 py-1 text-[12.5px] text-text-secondary transition-colors hover:bg-surface-hover hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
                          >
                            {suggestion}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : messagesLoading ? (
                  <Loader2 size={20} className="animate-spin text-text-muted" />
                ) : (
                  <p className="text-sm text-text-muted">Send a message to start the conversation.</p>
                )}
              </div>
            )}
            <div className={`${messagesWidthClass} min-w-0 space-y-5`}>
              {/* Older history loads on demand — the transcript only fetches
                  the newest RECENT_MESSAGES_LIMIT rows up front. */}
              {hasEarlier && messages.length > 0 && (
                <div className="flex justify-center">
                  <button
                    type="button"
                    onClick={handleLoadEarlier}
                    disabled={loadingEarlier}
                    className="inline-flex items-center gap-1.5 rounded-full border border-border bg-white px-3 py-1 text-xs text-text-secondary shadow-sm transition-colors hover:bg-surface-hover hover:text-text-primary disabled:opacity-60 dark:bg-surface"
                  >
                    {loadingEarlier ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : (
                      <History size={12} />
                    )}
                    <span>{loadingEarlier ? "Loading earlier messages…" : "Show earlier messages"}</span>
                  </button>
                </div>
              )}
              {messages.map((msg, idx) => {
                const isOtherAgent = msg.respondingAgentId && msg.respondingAgentId !== selectedAgentId;
                const msgAgentName = isOtherAgent ? msg.metadata?.respondingAgentName ?? "Agent" : selectedAgent?.name;
                const msgAgentAvatar = isOtherAgent ? (msg.metadata?.respondingAgentAvatarUrl ?? null) : selectedAgent?.avatarUrl;
                // Empty assistant rows must never render as blank bubbles.
                // A fresh row with streaming=true is a turn in flight on
                // another tab/device — show a working indicator (when this
                // tab streams, the live bubble below covers it). Anything
                // else empty is an orphan: skip it; the server's stream
                // sweeper rewrites it with an honest note shortly.
                if (msg.role === "assistant" && msg.content.trim() === "") {
                  if (msg.metadata?.streaming && !streamingHere) {
                    return (
                      <InFlightBubble
                        key={msg.id}
                        name={msgAgentName ?? "Agent"}
                        avatarUrl={msgAgentAvatar}
                      />
                    );
                  }
                  return null;
                }
                // Retry resends the user message immediately preceding this
                // assistant error reply. Only enabled for the most recent
                // failed reply so older errors stay informational.
                const isLastMessage = idx === messages.length - 1;
                let onRetry: (() => void) | undefined;
                if (isLastMessage && msg.role === "assistant" && isAgentErrorContent(msg.content) && !activeSessionBusy) {
                  const priorUser = [...messages.slice(0, idx)].reverse().find((m) => m.role === "user");
                  if (priorUser) {
                    onRetry = () => {
                      setInput(priorUser.content);
                      requestAnimationFrame(() => handleSend());
                    };
                  }
                }
                return (
                  <MessageBubble
                    key={msg.id}
                    message={msg}
                    agentAvatarUrl={msgAgentAvatar}
                    agentName={msgAgentName}
                    showAgentLabel={!!isOtherAgent}
                    respondingAgentId={msg.respondingAgentId || selectedAgentId || undefined}
                    onRetry={onRetry}
                    onCopyMenu={handleCopyMenu}
                  />
                );
              })}

              {/* Optimistic user message */}
              {streamingHere && optimisticUserMsg && !optimisticUserIsPersisted && (
                <div className="flex items-start gap-3 flex-row-reverse">
                  <UserChatAvatar userId={authUser?.id} displayName={authUser?.name} />
                  <div className="max-w-[80%] break-words rounded-[20px_20px_4px_20px] bg-accent px-4 py-2.5 text-sm text-white whitespace-pre-wrap [overflow-wrap:anywhere] lg:max-w-[88%]">
                    {optimisticUserMsg}
                  </div>
                </div>
              )}

              {/* Streaming assistant response — boxless: content sits directly
                  on the canvas so only the user's messages read as bubbles */}
              {streamingHere && (
                <div className="flex items-start gap-3">
                  <AgentChatAvatar avatarUrl={streamingAgent?.avatarUrl ?? selectedAgent?.avatarUrl} name={streamingAgent?.name ?? selectedAgent?.name} />
                  <div className="min-w-0 max-w-[90%] lg:max-w-[92%]">
                    <div className="pt-0.5 text-sm text-text-primary">
                      <div className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-text-secondary">
                        <span className="truncate">{streamingAgent?.name ?? selectedAgent?.name ?? "Agent"}</span>
                      </div>
                      <AgentTurn events={activityEvents} content={streamingContent} streaming />
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Input bar */}
          <div className="relative shrink-0 bg-background px-6 py-3 lg:px-10 max-sm:px-3">
            {/* Jump to latest — appears when scrolled up in a long conversation */}
            {showScrollButton && messages.length > 0 && (
              <button
                type="button"
                onClick={scrollToBottom}
                aria-label="Scroll to latest messages"
                className="absolute left-1/2 -top-9 z-10 flex h-7 w-7 -translate-x-1/2 items-center justify-center rounded-full border border-border bg-white text-text-secondary shadow-md transition-colors hover:bg-surface-hover hover:text-text-primary dark:bg-surface"
              >
                <ChevronDown size={15} />
              </button>
            )}
            <div className={chatWidthClass}>
              {/* Failed to create the session on first send (draft mode) */}
              {newChatError && (
                <div className="mb-2 flex items-center gap-2 rounded-lg bg-red-50 px-3 py-1.5 text-xs text-red-700 dark:bg-red-900/20 dark:text-red-300">
                  <X size={12} />
                  <span>{newChatError}</span>
                </div>
              )}
              {/* Skill upload status banner */}
              {skillUploadState.kind === "success" && (
                <div className="mb-2 flex items-center gap-2 rounded-lg bg-accent/10 px-3 py-1.5 text-xs text-accent">
                  <Wand2 size={12} />
                  <span>
                    Skill {skillUploadState.created ? "registered" : "updated"}: <strong>{skillUploadState.name}</strong> — available in the slash menu and tenant Skills directory.
                  </span>
                </div>
              )}
              {skillUploadState.kind === "error" && (
                <div className="mb-2 flex items-center gap-2 rounded-lg bg-red-50 px-3 py-1.5 text-xs text-red-700 dark:bg-red-900/20 dark:text-red-300">
                  <X size={12} />
                  <span>Skill upload failed: {skillUploadState.message}</span>
                </div>
              )}
              {skillUploadState.kind === "uploading" && (
                <div className="mb-2 flex items-center gap-2 rounded-lg bg-surface-hover px-3 py-1.5 text-xs text-text-secondary">
                  <Loader2 size={12} className="animate-spin" />
                  <span>Registering skill from {skillUploadState.filename}…</span>
                </div>
              )}
              {sharedSessionBusy && (
                <div className="mb-2 flex items-center gap-2 rounded-lg bg-surface-hover px-3 py-1.5 text-xs text-text-secondary">
                  <Loader2 size={12} className="animate-spin" />
                  <span>This shared chat is responding for another user. You can switch to another chat and keep working.</span>
                </div>
              )}
              {/* Attachment preview */}
              {attachments.length > 0 && (
                <div className="mb-2 flex flex-wrap gap-2">
                  {attachments.map((file, i) => (
                    <div key={i} className="group relative flex items-center gap-1.5 rounded-lg bg-surface-hover px-2.5 py-1.5 text-xs text-text-secondary">
                      {file.type.startsWith("image/") ? (
                        <PendingImageThumb file={file} />
                      ) : (
                        <FileText size={12} />
                      )}
                      <span className="max-w-[120px] truncate">{file.name}</span>
                      <span className="text-text-muted">({Math.round(file.size / 1024)}KB)</span>
                      <button
                        onClick={() => removeAttachment(i)}
                        className="text-text-muted hover:text-text-primary"
                        title={`Remove ${file.name}`}
                        aria-label={`Remove ${file.name}`}
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div
                ref={inputContainerRef}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={cn(
                  "relative flex flex-col rounded-2xl border bg-white px-4 pt-3 pb-2 shadow-sm focus-within:border-accent focus-within:shadow-md transition-all dark:bg-surface",
                  isDragging ? "border-accent bg-accent/5" : "border-border",
                )}
              >
                {/* @Mention autocomplete dropdown — agents + workspace files */}
                {mentionState?.active && mentionCandidates.length > 0 && inputContainerRef.current && createPortal(
                  <MentionDropdown
                    candidates={mentionCandidates}
                    selectedIndex={mentionState.selectedIndex}
                    anchorEl={inputContainerRef.current}
                    onSelect={insertMention}
                  />,
                  document.body,
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={handleFileChange}
                  accept="image/*,.pdf,.txt,.json,.doc,.docx,.xls,.xlsx,.csv"
                />
                <input
                  ref={skillFileInputRef}
                  type="file"
                  className="hidden"
                  onChange={handleSkillFileChange}
                  accept=".md,.markdown,text/markdown"
                />
                <div className="relative">
                  {input && (
                    <MentionInputHighlight
                      ref={highlightRef}
                      value={input}
                      agents={agents}
                      files={workspaceFiles}
                      className="absolute inset-0 min-h-[60px] max-h-[240px] overflow-hidden text-[14.5px] leading-6 lg:min-h-[80px]"
                    />
                  )}
                  <textarea
                    ref={inputRef}
                    value={input}
                    onChange={handleInputChange}
                    onPaste={handlePaste}
                    onScroll={(e) => {
                      // Keep the highlight mirror in sync with the textarea's scroll.
                      const el = highlightRef.current;
                      if (el) {
                        el.scrollTop = e.currentTarget.scrollTop;
                        el.scrollLeft = e.currentTarget.scrollLeft;
                      }
                    }}
                    onKeyDown={(e) => {
                      if (mentionState?.active && mentionCandidates.length > 0) {
                        if (e.key === "ArrowDown") {
                          e.preventDefault();
                          setMentionState((prev) => prev ? { ...prev, selectedIndex: Math.min(prev.selectedIndex + 1, mentionCandidates.length - 1) } : null);
                          return;
                        }
                        if (e.key === "ArrowUp") {
                          e.preventDefault();
                          setMentionState((prev) => prev ? { ...prev, selectedIndex: Math.max(prev.selectedIndex - 1, 0) } : null);
                          return;
                        }
                        if (e.key === "Enter" || e.key === "Tab") {
                          e.preventDefault();
                          insertMention(mentionCandidates[mentionState.selectedIndex]);
                          return;
                        }
                        if (e.key === "Escape") {
                          e.preventDefault();
                          setMentionState(null);
                          return;
                        }
                      }
                      if (e.key === "Enter" && !e.shiftKey) {
                        // Swallowed while the agent is answering. handleSend
                        // guards this too, but preventing the default here
                        // stops Enter dropping a newline into a draft the
                        // user meant to send.
                        e.preventDefault();
                        if (activeSessionBusy) return;
                        handleSend();
                      }
                    }}
                    placeholder={
                      activeSessionBusy
                        ? sharedSessionBusy
                          ? "This shared chat is responding for another user — you can still type..."
                          : `${selectedAgent?.name ?? "Agent"} is responding — you can still type...`
                        : `Message ${selectedAgent?.name ?? "agent"}... (@ for agents & files)`
                    }
                    /* Deliberately NOT disabled while the agent answers. A
                       thought arrives while you are reading the reply, and
                       locking the box means holding it in your head or losing
                       it. Only SENDING is blocked — by the Enter handler
                       above, by handleSend's own guard, and by the Stop button
                       standing where Send would be. */
                    rows={1}
                    data-tour="composer"
                    className={cn(
                      // Native scrollbar hidden so the textarea and the highlight
                      // mirror wrap to the exact same width — otherwise the
                      // scrollbar gutter makes the textarea wrap taller and the
                      // two layers drift apart when scrolled. Scroll via wheel /
                      // caret; onScroll keeps the mirror in sync.
                      "relative z-10 w-full resize-none bg-transparent text-[14.5px] leading-6 focus:outline-none min-h-[60px] max-h-[240px] disabled:cursor-not-allowed disabled:opacity-60 lg:min-h-[80px] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
                      input && "text-transparent caret-text-primary placeholder:text-text-muted",
                    )}
                  />
                </div>
                <div className="mt-1 flex items-center justify-between">
                  <div className="flex items-center gap-1">
                    <ChatToolbarTooltip label={attachments.length >= 5 ? "Maximum 5 files" : "Attach files"}>
                      <button
                        onClick={() => {
                          setSlashMenuOpen(false);
                          handleFileSelect();
                        }}
                        disabled={activeSessionBusy || attachments.length >= 5}
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-text-secondary transition-colors hover:bg-surface-hover hover:text-text-primary disabled:cursor-not-allowed disabled:opacity-50"
                        title={attachments.length >= 5 ? "Maximum 5 files" : "Attach files to this message"}
                        data-tour="attach"
                      >
                        <Plus size={18} />
                      </button>
                    </ChatToolbarTooltip>
                    {/* Upload-skill button — registers a SKILL.md with this
                        agent + the tenant Skills directory. Lives next to the
                        Plus/attach button so it's discoverable from the input
                        bar without diving into a menu. */}
                    <ChatToolbarTooltip label="Upload a skill">
                      <button
                        onClick={handleSkillFileSelect}
                        disabled={
                          activeSessionBusy ||
                          !selectedAgentId ||
                          skillUploadState.kind === "uploading"
                        }
                        title="Upload a SKILL.md skill for this agent"
                        className={cn(
                          "flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-colors",
                          skillUploadState.kind === "uploading"
                            ? "text-text-muted"
                            : "text-text-secondary hover:text-text-primary hover:bg-surface-hover",
                        )}
                      >
                        {skillUploadState.kind === "uploading" ? (
                          <Loader2 size={16} className="animate-spin" />
                        ) : (
                          <Wand2 size={16} />
                        )}
                      </button>
                    </ChatToolbarTooltip>
                    {/* Tools (MCPs) menu — toggle MCPs and individual tools per agent */}
                    <AgentToolsMenu agentId={selectedAgentId} disabled={activeSessionBusy} />
                    {/* / button with slash commands menu (skill shortcut) */}
                    <div ref={slashMenuRef} className="relative">
                      <ChatToolbarTooltip label="Use a skill shortcut">
                        <button
                          onClick={() => setSlashMenuOpen((o) => !o)}
                          disabled={activeSessionBusy}
                          data-tour="composer-skill"
                          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-border text-text-secondary transition-colors hover:border-text-secondary hover:bg-surface-hover hover:text-text-primary"
                          title="Use a skill shortcut"
                        >
                          <Slash size={13} />
                        </button>
                      </ChatToolbarTooltip>
                      {slashMenuOpen && (
                        <div data-tour="skill-popup" className="absolute bottom-full left-0 mb-2 w-[min(260px,calc(100vw-2rem))] max-h-[320px] overflow-y-auto rounded-xl border border-border bg-white shadow-lg dark:bg-surface z-10 py-2">
                          <div className="px-3 pb-1.5">
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-text-muted">Use a skill</p>
                          </div>
                          {skillsLoading ? (
                            <p className="px-3 py-2 text-xs text-text-muted">Loading skills…</p>
                          ) : slashMenuSkills.length === 0 ? (
                            <p className="px-3 py-2 text-xs text-text-muted">
                              No skills assigned to this agent. Assign one from the agent's Skills tab.
                            </p>
                          ) : (
                            slashMenuSkills.map((skill) => (
                              <button
                                key={skill.id}
                                disabled={!!skill.disabledReason}
                                title={skill.disabledReason ?? "Use " + skill.name}
                                onClick={() => {
                                  if (skill.disabledReason) return;
                                  setSlashMenuOpen(false);
                                  setInput((prev) => {
                                    const prefix = prev.length > 0 && !prev.endsWith(" ") ? prev + " " : prev;
                                    return `${prefix}Use the "${skill.name}" skill: `;
                                  });
                                  // Refocus the textarea so the user can keep typing
                                  setTimeout(() => inputRef.current?.focus(), 0);
                                }}
                                className={cn(
                                  "flex w-full items-start gap-2 px-3 py-2 text-left text-sm transition-colors",
                                  skill.disabledReason
                                    ? "cursor-not-allowed text-text-muted opacity-70"
                                    : "text-text-primary hover:bg-surface-hover",
                                )}
                              >
                                <Sparkles
                                  size={13}
                                  className={cn("mt-0.5 shrink-0", skill.disabledReason ? "text-text-muted" : "text-accent")}
                                />
                                <span className="min-w-0 flex-1">
                                  <span className="block truncate">{skill.name}</span>
                                  {skill.disabledReason && (
                                    <span className="mt-0.5 block truncate text-[11px] text-text-muted">
                                      {skill.disabledReason}
                                    </span>
                                  )}
                                </span>
                              </button>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                    {/* Workspace files browser — now after the skill shortcut, icon only */}
                    <WorkspaceTriggerButton agentId={selectedAgentId} />
                  </div>
                  {streamingHere ? (
                    <ChatToolbarTooltip label="Stop response">
                      <button
                        onClick={handleStop}
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-text-muted/20 text-text-secondary hover:bg-text-muted/30 transition-colors"
                        aria-label={`Stop ${streamingAgent?.name ?? selectedAgent?.name ?? "agent"}`}
                        title={`Stop ${streamingAgent?.name ?? selectedAgent?.name ?? "response"}`}
                      >
                        <Square size={14} />
                      </button>
                    </ChatToolbarTooltip>
                  ) : (
                    <ChatToolbarTooltip label="Send message">
                      <button
                        disabled={activeSessionBusy || (!input.trim() && attachments.length === 0)}
                        onClick={handleSend}
                        aria-label={`Send message to ${selectedAgent?.name ?? "agent"}`}
                        title={`Send message to ${selectedAgent?.name ?? "agent"}`}
                        className={cn(
                          "flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-colors",
                          !activeSessionBusy && (input.trim() || attachments.length > 0)
                            ? "bg-accent text-white hover:bg-accent-hover"
                            : "bg-text-muted/20 text-text-muted cursor-not-allowed",
                        )}
                      >
                        <ArrowUp size={16} />
                      </button>
                    </ChatToolbarTooltip>
                  )}
                </div>
                {/* Only once there is something waiting to go. Saying "you
                    cannot send" to someone who has not typed anything is
                    noise. */}
                {activeSessionBusy && (input.trim() || attachments.length > 0) && (
                  <p className="mt-2 text-xs text-text-muted">
                    {streamingHere
                      ? "Send is paused until the reply finishes. Press Stop if you want to send this now."
                      : "Send is paused until the current reply finishes."}
                  </p>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
    <ArtifactPanel />
    </div>
    </ArtifactProvider>
  );
}

function formatTime(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

/**
 * Server-side fallback strings that get persisted as assistant messages when
 * the upstream gateway returns no usable content. The UI treats these as
 * inline error states (icon + retry) rather than normal agent replies.
 */
const AGENT_ERROR_MESSAGES = new Set<string>([
  "No response from agent.",
  "Agent could not get a response from the AI model. The service may be temporarily overloaded or rate-limited. Try again in a moment.",
  // Written by the disconnect-abort path and the orphaned-stream sweeper
  // (server/src/services/chat-stream-health.ts) — keep the text in sync.
  "The agent was interrupted before it sent a reply. Send your message again to continue.",
]);
function isAgentErrorContent(content: string | null | undefined): boolean {
  if (!content) return false;
  if (AGENT_ERROR_MESSAGES.has(content)) return true;
  return content.startsWith("Agent is not deployed");
}

/**
 * Placeholder for an assistant turn that is in flight on another tab or
 * device: the polled row exists (pre-inserted, metadata.streaming=true)
 * but has no content yet. Rendering the raw row produced a blank bubble —
 * agent name + timestamp around nothing — which read as a lost reply.
 */
function InFlightBubble({ name, avatarUrl }: { name: string; avatarUrl?: string | null }) {
  return (
    <div className="flex items-start gap-3">
      <AgentChatAvatar avatarUrl={avatarUrl} name={name} />
      <div className="min-w-0 max-w-[90%] lg:max-w-[92%]">
        <div className="pt-0.5 text-sm text-text-primary">
          <div className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-text-secondary">
            <span className="truncate">{name}</span>
          </div>
          <div className="flex items-center gap-2 text-sm font-medium text-text-primary">
            <Loader2 size={14} className="animate-spin text-accent" />
            <span>{name} is still working on this…</span>
          </div>
          <p className="m-0 mt-1 text-xs text-text-muted">The reply will appear here when it&apos;s ready.</p>
        </div>
      </div>
    </div>
  );
}

// Memoized: with a windowed transcript there are still up to a few hundred
// bubbles mounted, each with a markdown tree — the parent re-renders every
// second while streaming (elapsed timer) and on every composer keystroke, so
// unchanged bubbles must bail out. Message rows keep referential identity
// across polls via TanStack Query's structural sharing.
const MessageBubble = memo(function MessageBubble({ message, agentAvatarUrl, agentName, showAgentLabel, respondingAgentId, onRetry, onCopyMenu }: { message: ChatMessage; agentAvatarUrl?: string | null; agentName?: string; showAgentLabel?: boolean; respondingAgentId?: string; onRetry?: () => void; onCopyMenu?: (e: React.MouseEvent<HTMLDivElement>, content: string) => void }) {
  const isUser = message.role === "user";
  const isError = !isUser && isAgentErrorContent(message.content);
  const displayAgentName = agentName || "Agent";
  const attachments = (message.metadata?.attachments ?? []) as ChatFileAttachment[];
  const artifacts = useMemo(
    () => (!isUser && !isError ? detectArtifacts(message.content) : []),
    [message.content, isUser, isError],
  );
  useArtifactAutoOpen({
    messageId: message.id,
    createdAt: message.createdAt,
    artifacts,
    agentId: respondingAgentId,
  });
  return (
    <div
      className={cn("flex items-start gap-3", isUser && "flex-row-reverse")}
      onContextMenu={onCopyMenu ? (e) => onCopyMenu(e, message.content) : undefined}
    >
      {isUser ? (
        <UserChatAvatar userId={message.senderUserId} displayName={message.senderName} />
      ) : isError ? (
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-red-500/10 text-red-600 dark:text-red-400">
          <AlertTriangle size={14} />
        </div>
      ) : (
        <AgentChatAvatar avatarUrl={agentAvatarUrl} name={agentName} />
      )}
      <div className={cn("min-w-0", isUser ? "max-w-[80%] lg:max-w-[88%]" : "max-w-[90%] lg:max-w-[92%]")}>
        {/* Attachments sit above the message text: the picture is the subject
            of the turn and the words are the caption, so it reads top-down. */}
        {attachments.length > 0 && (
          <div className={cn("mb-1.5 flex flex-wrap gap-1.5", isUser && "justify-end")}>
            {attachments.map((file) => (
              <FileAttachmentChip key={file.id} file={file} />
            ))}
          </div>
        )}
        {isUser ? (
          <div className="break-words rounded-[20px_20px_4px_20px] bg-accent px-4 py-2.5 text-sm text-white whitespace-pre-wrap [overflow-wrap:anywhere]">
            {message.content}
          </div>
        ) : isError ? (
          <div className="rounded-2xl border border-red-500/30 bg-red-500/5 px-3.5 py-2.5 text-[13px] text-text-secondary">
            <p className="m-0 flex items-start gap-1.5">
              <span className="mt-0.5 text-[11px] font-semibold uppercase tracking-wider text-red-600 dark:text-red-400">
                Delivery failed
              </span>
            </p>
            <p className="m-0 mt-1 text-text-primary">{message.content}</p>
            {onRetry && (
              <button
                type="button"
                onClick={onRetry}
                className="mt-2 inline-flex items-center gap-1 rounded-md border border-red-500/30 px-2 py-1 text-[11px] font-medium text-red-600 hover:bg-red-500/10 dark:text-red-400"
              >
                <RotateCcw size={11} />
                Retry
              </button>
            )}
          </div>
        ) : (
          // Agent replies are boxless — the response lands directly on the
          // canvas (avatar + name label kept) so only user messages read as
          // shaded bubbles.
          <div className="pt-0.5 text-sm text-text-primary">
            <div className={cn(
              "mb-1.5 flex items-center gap-1.5 text-xs font-medium",
              showAgentLabel ? "text-accent" : "text-text-secondary",
            )}>
              {showAgentLabel && <AtSign size={10} className="shrink-0" />}
              <span className="truncate">{displayAgentName}</span>
            </div>
            <AgentTurn
              events={(message.metadata?.toolSteps ?? []).map((s) => ({
                toolUseId: s.toolUseId,
                name: s.name,
                status: s.status,
                args: s.args,
                resultPreview: s.result,
                error: s.error,
                durationMs: s.durationMs,
                timestamp: s.timestamp ?? 0,
              }))}
              content={message.content}
              streaming={false}
            />
            <span className="mt-2 block text-[10px] text-text-muted">
              {formatTime(message.createdAt)}
            </span>
          </div>
        )}
        {artifacts.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            {artifacts.map((artifact, i) => (
              <ArtifactButton key={i} artifact={artifact} agentId={respondingAgentId} />
            ))}
          </div>
        )}
        {isUser && (
          <span className="mt-1 block text-right text-[10px] text-text-muted">
            {formatTime(message.createdAt)}
          </span>
        )}
      </div>
    </div>
  );
});
